import { StyleProvider } from '@ant-design/cssinjs';
import { ConfigProvider } from 'antd';
import en from 'antd/locale/en_GB';
import { useLayoutEffect } from 'react';
import type { ReactNode } from 'react';

import NotificationCloseIcon from '@/shared/assets/icons/notification-close.svg?react';
import { darkTheme, lightTheme } from '@/shared/config/theme/tokens';
import { useAppSelector } from '@/shared/model/storeHooks';

import styles from './ThemeProvider.module.css';

// antd writes the tokens as CSS variables under this class. It is put on <html>
// so that our own markup, the body and every portal read the same variables.
const THEME_CLASS = 'app-theme';

const modalConfig = {
  centered: true,
  closeIcon: <NotificationCloseIcon color="var(--ant-color-text)" />,
  // The dialog itself; on phones it becomes a sheet at the bottom
  className: styles.dialog,
  classNames: {
    wrapper: styles.wrapper,
    container: styles.container,
    header: styles.header,
    body: styles.body,
    footer: styles.footer,
    close: styles.close,
  },
};

const formConfig = {
  requiredMark: false,
  classNames: { help: styles.formHelp },
};

const inputConfig = {
  classNames: { prefix: styles.inputPrefix, input: styles.inputValue },
};

const tooltipConfig = { classNames: { container: styles.tooltipContainer } };

// Inside the store provider: the antd theme follows the chosen theme mode
export function ThemeProvider({ children }: { children: ReactNode }) {
  const mode = useAppSelector((state) => state.nav.theme);
  const config = mode === 'dark' ? darkTheme : lightTheme;

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add(THEME_CLASS);
    root.style.colorScheme = mode;
  }, [mode]);

  // antd's styles go into a CSS cascade layer, so the app's own CSS Modules
  // win over them without `!important` or `.ant-*` selectors
  return (
    <StyleProvider layer>
      <ConfigProvider
        locale={en}
        modal={modalConfig}
        form={formConfig}
        input={inputConfig}
        tooltip={tooltipConfig}
        theme={{ ...config, cssVar: { key: THEME_CLASS } }}
      >
        {children}
      </ConfigProvider>
    </StyleProvider>
  );
}
